import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail, RotateCcw, Eye, ChevronLeft, Send, Copy, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BlogEditor from "@/components/admin/BlogEditor";
const CATEGORY_LABELS = {
  invoice: "Invoice",
  reminder: "Reminder",
  confirmation: "Confirmation",
  refund: "Refund",
};

export default function EmailTemplatesTab() {
  const [editingKey, setEditingKey] = useState(null);
  const { data: templates = [], refetch, isLoading } = useQuery({
    queryKey: ["email-templates"],
    queryFn: async () => (await api.get("/admin/email-templates")).data,
  });

  if (isLoading) {
    return <div className="rounded-2xl border border-gg-border bg-gg-surface p-12 text-center text-gg-muted">Loading templates…</div>;
  }

  if (editingKey) {
    return (
      <EmailTemplateEditor
        templateKey={editingKey}
        onClose={() => { setEditingKey(null); refetch(); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-gg-border bg-gg-surface p-5">
        <div className="flex items-start gap-3">
          <Mail size={20} className="text-gg-green mt-1 shrink-0" />
          <div>
            <h3 className="font-heading text-lg text-[var(--gg-text)]">Automated emails</h3>
            <p className="text-sm text-gg-muted mt-1">
              Edit the subject line and body content sent to parents automatically.
              Use <code className="text-xs bg-gg-bg px-1 rounded">{"{{variable_name}}"}</code> to insert dynamic values.
              Toggle a template <strong>off</strong> to disable that email entirely without deleting it.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {templates.map((t) => (
          <TemplateRow key={t.key} template={t} onEdit={() => setEditingKey(t.key)} />
        ))}
      </div>
    </div>
  );
}

function TemplateRow({ template, onEdit }) {
  const isModified = template.is_subject_modified || template.is_body_modified;
  return (
    <div
      className="rounded-xl border border-gg-border bg-gg-surface p-4 hover:border-gg-green/60 transition-colors cursor-pointer"
      onClick={onEdit}
      data-testid={`email-tmpl-row-${template.key}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-gg-muted bg-gg-bg border border-gg-border rounded px-2 py-0.5">
              {CATEGORY_LABELS[template.category] || template.category}
            </span>
            <span className="font-heading text-lg text-[var(--gg-text)]">{template.name}</span>
            {!template.enabled && (
              <span className="text-[10px] uppercase tracking-wider text-[#B24C4C] bg-[#B24C4C]/10 border border-[#B24C4C]/30 rounded px-2 py-0.5" data-testid={`email-tmpl-disabled-${template.key}`}>
                Disabled
              </span>
            )}
            {isModified && (
              <span className="text-[10px] uppercase tracking-wider text-gg-brown bg-gg-brown/10 border border-gg-brown/30 rounded px-2 py-0.5">
                Customized
              </span>
            )}
          </div>
          <p className="text-xs text-gg-muted mt-1">{template.description}</p>
          <p className="text-sm text-[var(--gg-text)] mt-2 truncate">
            <span className="text-gg-muted">Subject:</span> {template.subject}
          </p>
        </div>
        <button
          type="button"
          className="btn-pill btn-outline shrink-0 text-xs"
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          data-testid={`email-tmpl-edit-${template.key}`}
        >
          Edit
        </button>
      </div>
    </div>
  );
}

function EmailTemplateEditor({ templateKey, onClose }) {
  const [template, setTemplate] = useState(null);
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/admin/email-templates/${templateKey}`);
        if (!alive) return;
        setTemplate(data);
        setSubject(data.subject);
        setBodyHtml(data.body_html);
        setEnabled(data.enabled);
      } catch (e) {
        toast.error("Could not load template");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [templateKey]);

  // Live preview with debounce
  useEffect(() => {
    if (!template) return;
    const id = setTimeout(async () => {
      try {
        const { data } = await api.post(`/admin/email-templates/${templateKey}/preview`, {
          subject, body_html: bodyHtml,
        });
        setPreview(data);
      } catch {}
    }, 400);
    return () => clearTimeout(id);
  }, [subject, bodyHtml, template, templateKey]);

  const isDirty = useMemo(() =>
    template && (subject !== template.subject || bodyHtml !== template.body_html || enabled !== template.enabled),
    [template, subject, bodyHtml, enabled]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/email-templates/${templateKey}`, {
        subject, body_html: bodyHtml, enabled,
      });
      setTemplate(data);
      toast.success("Template saved — the next email will use it");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!confirm(`Reset "${template.name}" to the default wording? Your customizations will be lost.`)) return;
    try {
      const { data } = await api.post(`/admin/email-templates/${templateKey}/reset`);
      setTemplate(data);
      setSubject(data.subject);
      setBodyHtml(data.body_html);
      toast.success("Reset to default");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const { data } = await api.post(`/admin/email-templates/${templateKey}/send-test`, {
        subject, body_html: bodyHtml,
      });
      toast.success(`Test sent to ${data.sent_to} — check your inbox`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed — check email service configuration");
    } finally {
      setSendingTest(false);
    }
  };

  const copyVariable = async (name) => {
    const varStr = `{{${name}}}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(varStr);
      } else {
        // Fallback for non-secure contexts (older browsers, iframes without permission)
        const ta = document.createElement("textarea");
        ta.value = varStr;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast(`Copied ${varStr}`);
    } catch {
      // Clipboard blocked (permissions, iframe, etc.) — show the string so admin can select manually
      toast(`Copy blocked by browser — select and copy manually: ${varStr}`);
    }
  };

  if (loading || !template) {
    return <div className="rounded-2xl border border-gg-border bg-gg-surface p-12 text-center text-gg-muted">Loading…</div>;
  }

  return (
    <div className="space-y-4" data-testid={`email-tmpl-editor-${templateKey}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button type="button" onClick={onClose} className="btn-pill btn-outline text-sm" data-testid="email-tmpl-back">
          <ChevronLeft size={14} /> Back to list
        </button>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              data-testid={`email-tmpl-enabled-${templateKey}`}
            />
            <span className={enabled ? "text-gg-green" : "text-[#B24C4C]"}>
              {enabled ? "Enabled — will be sent" : "Disabled — will NOT be sent"}
            </span>
          </label>
          <button type="button" onClick={reset} className="btn-pill btn-outline text-sm" data-testid="email-tmpl-reset">
            <RotateCcw size={14} /> Reset to default
          </button>
          <button type="button" onClick={sendTest} disabled={sendingTest} className="btn-pill btn-outline text-sm disabled:opacity-60" data-testid="email-tmpl-send-test">
            <Send size={14} /> {sendingTest ? "Sending…" : "Send me a test"}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !isDirty}
            className="btn-pill btn-primary text-sm disabled:opacity-60"
            data-testid="email-tmpl-save"
          >
            {saving ? "Saving…" : isDirty ? "Save changes" : "Saved"}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-gg-border bg-gg-surface p-4">
        <h2 className="font-heading text-2xl text-[var(--gg-text)]">{template.name}</h2>
        <p className="text-sm text-gg-muted mt-1">{template.description}</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* LEFT: editor */}
        <div className="space-y-4">
          <div className="rounded-xl border border-gg-border bg-gg-surface p-4">
            <Label>Subject line</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1.5"
              placeholder="e.g. Registration confirmed — {{class_title}}"
              data-testid="email-tmpl-subject"
            />
          </div>

          <div className="rounded-xl border border-gg-border bg-gg-surface p-4">
            <Label>Body (rich text)</Label>
            <div className="mt-1.5 rounded-lg border border-gg-border bg-gg-bg" data-testid="email-tmpl-body-editor">
              <BlogEditor value={bodyHtml} onChange={setBodyHtml} />
            </div>
            <p className="text-[11px] text-gg-muted mt-2 flex items-start gap-1">
              <AlertCircle size={12} className="mt-0.5 shrink-0" />
              <span>The email will be wrapped with a Graceful Garden header/footer automatically. Only edit the message body here.</span>
            </p>
          </div>

          <div className="rounded-xl border border-gg-border bg-gg-surface p-4">
            <Label>Available variables — click to copy</Label>
            <p className="text-xs text-gg-muted mt-1 mb-3">Paste any of these into the subject or body. Unknown variables are left visible so you notice typos.</p>
            <div className="grid sm:grid-cols-2 gap-1.5">
              {(template.variables || []).map((v) => (
                <button
                  key={v.name}
                  type="button"
                  onClick={() => copyVariable(v.name)}
                  className="text-left text-xs rounded-md border border-gg-border bg-gg-bg p-2 hover:border-gg-green transition-colors"
                  data-testid={`email-tmpl-var-${v.name}`}
                >
                  <code className="font-mono text-gg-green">{`{{${v.name}}}`}</code>
                  <div className="text-[11px] text-gg-muted mt-0.5">{v.desc}</div>
                  {v.example && <div className="text-[11px] text-gg-brown mt-0.5">Example: <em>{v.example}</em></div>}
                </button>
              ))}
              <button
                type="button"
                onClick={() => copyVariable("location_block")}
                className="text-left text-xs rounded-md border border-gg-green/40 bg-gg-green/5 p-2 hover:border-gg-green transition-colors"
                data-testid="email-tmpl-var-location_block"
              >
                <code className="font-mono text-gg-green">{"{{location_block}}"}</code>
                <div className="text-[11px] text-gg-muted mt-0.5">Auto-renders "at our home" / "off-site" / "in-home visit" based on the class or session settings</div>
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT: preview */}
        <div className="lg:sticky lg:top-4 self-start">
          <div className="rounded-xl border border-gg-border bg-white overflow-hidden shadow-sm" data-testid="email-tmpl-preview">
            <div className="bg-gg-bg border-b border-gg-border p-3">
              <div className="flex items-center gap-2 text-xs text-gg-muted">
                <Eye size={12} /> LIVE PREVIEW (with sample data)
              </div>
              <div className="mt-2 text-[13px] text-gg-muted">
                From: <strong className="text-[var(--gg-text)]">Graceful Garden Coaching</strong>
              </div>
              <div className="text-[13px] text-gg-muted">
                Subject: <strong className="text-[var(--gg-text)]" data-testid="email-tmpl-preview-subject">{preview?.subject_rendered || "…"}</strong>
              </div>
            </div>
            <div
              className="p-4 text-sm text-[#2A2A2A] bg-[#FDFCF8] min-h-[400px]"
              dangerouslySetInnerHTML={{ __html: preview?.body_rendered || "" }}
              data-testid="email-tmpl-preview-body"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
